import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';

import {
  DashboardDevice,
  DashboardInstallationData,
  DashboardMainDevice,
  DashboardSmartNote,
  SubscriptionPlan,
} from '@vi-iot/client';

import {
  ACTIVE_ERROR_TYPE,
  BookingStore,
  DashboardFilter,
  KanbanBoardStore,
  Platform,
  RemoteConfigStore,
  UpgradePackageButtonData,
  getDevicePlatformBasingOnRoles,
  hasMainDeviceRole,
} from '@store';
import { HighestSubscriptionPlanPipe } from '../../+kanban-board/kanban-installation-list/pipes/highest-subscription-plan/highest-subscription-plan.pipe';

@Injectable({ providedIn: 'root' })
export class KanbanBoardHelperService {
  // Additional abstract layer between components (3) and store due to risk of race condition
  // Text input uses debounceTime and I find it risky to rely solely on store's data
  // as its data update is reflected in async way (reducer -> selector) and here we've 1 data source
  // It's not updated earlier because there's a need for comparison with old data to prevent redundant calls
  filterInputSearchValue$: BehaviorSubject<string> = new BehaviorSubject('');
  filtersSelectedInDropdown$: BehaviorSubject<DashboardFilter[]> = new BehaviorSubject([]);
  clearSearchInput$: Subject<void> = new Subject();
  private buttonFilterClicked$: Subject<void> = new Subject();

  constructor(
    private translateService: TranslateService,
    private kanbanBoardStore: KanbanBoardStore,
    private remoteConfigStore: RemoteConfigStore,
    private bookingStore: BookingStore
  ) {}

  getInstallationHeaderLabel(installation: DashboardInstallationData): string {
    if (installation.isMultiGateway) {
      return this.translateService.instant('+INSTALLATIONS.INSTALLATION.MULTIGATEWAY_HEADER_LABEL');
    } else if (installation.device?.marketingName) {
      return installation.device.marketingName;
    } else {
      const heaterOf = this.translateService.instant(
        '+INSTALLATIONS.INSTALLATIONS.INSTALLATION_OF'
      );
      const ownerName =
        installation?.owner?.firstName && installation?.owner?.lastName
          ? `${installation.owner.firstName} ${installation.owner.lastName}`
          : '';

      return `${heaterOf} ${ownerName}`;
    }
  }

  //   Booked package badge
  calculateIsMainPackageBadgeVisible(installation: DashboardInstallationData): boolean {
    if (!installation.isMultiGateway) {
      return [SubscriptionPlan.Pro, SubscriptionPlan.Plus].includes(
        HighestSubscriptionPlanPipe.getHighestDevicePlan(installation?.device)
      );
    }

    return false;
  }

  //   Upgrade to Pro badge
  getUpgradePackageData(
    installation: DashboardInstallationData
  ): Observable<UpgradePackageButtonData> {
    return combineLatest([
      this.remoteConfigStore.hasAccessToProPlusFeaturesByVkOrg$,
      this.remoteConfigStore.hasAccessToProPlusFeaturesByCompanyId$,
      this.bookingStore.hasAccessToBooking$,
    ]).pipe(
      filter(
        ([accessByVkOrg, accessByCompanyId, bookingAccess]) =>
          accessByVkOrg && accessByCompanyId && bookingAccess
      ),
      map(() => {
        if (installation.isStandalone) {
          return {
            platform: null,
            visible: false,
          };
        }
        if (installation.isMultiGateway || installation.isCascade) {
          const devicesWithoutPackage = installation.devices.filter((device) => {
            const plan = HighestSubscriptionPlanPipe.getHighestDevicePlan(device);

            return plan === SubscriptionPlan.Free || plan === SubscriptionPlan.Basic;
          });

          const mainDevicePlatforms = this.getPlatformOfMainDevices(installation.devices);
          const bookingDescriptionPlatform = this.getBookingDescriptionPlatform(
            mainDevicePlatforms
          );
          const isPlatformDefined =
            !!bookingDescriptionPlatform && bookingDescriptionPlatform !== Platform.None;

          // Display only when no device has paid package
          if (devicesWithoutPackage.length === installation.devices.length) {
            const isVisible = isPlatformDefined;

            return {
              visible: isVisible,
              platform: bookingDescriptionPlatform,
            };
          } else {
            return {
              visible: false,
              platform: bookingDescriptionPlatform,
            };
          }
        } else {
          const isNotProPackage = [
            SubscriptionPlan.Plus,
            SubscriptionPlan.Basic,
            SubscriptionPlan.Free,
          ].includes(HighestSubscriptionPlanPipe.getHighestDevicePlan(installation?.device));

          const mainDevicePlatform = this.getPlatformOfMainDevice(installation.device);
          const platform = this.getBookingDescriptionPlatform([mainDevicePlatform]);

          return {
            visible: isNotProPackage && platform !== Platform.None,
            platform,
          };
        }
      })
    );
  }

  isSingleDevice(installation: DashboardInstallationData): boolean {
    return installation?.devices?.length === 1;
  }

  getMainDevice(installation: DashboardInstallationData): DashboardDevice {
    return installation?.devices?.find((device) => installation.device?.vin === device?.vin);
  }

  calculateImagePath(installation: DashboardInstallationData): string {
    if (installation.isMultiGateway) {
      return '/assets/img/installation-multigateway.svg';
    }

    const vin = installation?.device?.vin;
    if (!vin) {
      return '';
    }

    const matchingMaterialImage =
      installation?.device?.pictogramImage || installation?.device?.materialImage;

    return matchingMaterialImage || '';
  }

  /* Method recommended for multi gateway installations. Iterate over all devices and platform of main devices. */
  private getPlatformOfMainDevices(devices: DashboardDevice[]): Platform[] {
    const platforms: Platform[] = [];
    devices.forEach((device) => {
      if (hasMainDeviceRole(device)) {
        platforms.push(getDevicePlatformBasingOnRoles(device.roles));
      }
    });

    return platforms;
  }

  /* Method recommended for NOT multi gateway installations. In such case only one property - device - needs to be checked. */
  private getPlatformOfMainDevice(device: DashboardMainDevice): Platform {
    return getDevicePlatformBasingOnRoles(device.roles);
  }

  /* Lack of Platform disables monetization because it's impossible to make sure which price is correct.
  Legacy is more important than E3 because it offers less functionalities for lower price
  */
  private getBookingDescriptionPlatform(platforms: Platform[]): Platform {
    if (platforms.length === 0) {
      return Platform.None;
    } else if (platforms.includes(Platform.Legacy)) {
      return Platform.Legacy;
    } else {
      return Platform.E3;
    }
  }

  isNextActionOnInfiniteScroll(event: Event, offset = 10): boolean {
    const eventTarget = event.target as HTMLElement;

    const visibleHeight = eventTarget.offsetHeight;
    const scrolledFromTop = eventTarget.scrollTop;
    const totalHeight = eventTarget.scrollHeight;

    return Math.ceil(visibleHeight + scrolledFromTop) >= totalHeight - offset;
  }

  isErrorSmartNote(smartNotes: DashboardSmartNote[]): boolean {
    return smartNotes?.some((smartNote) => smartNote.type === ACTIVE_ERROR_TYPE.DEVICE_ERROR);
  }

  getSelectedFiltersOnFilterButtonClick(): Observable<DashboardFilter[]> {
    return this.buttonFilterClicked$.pipe(
      withLatestFrom(this.kanbanBoardStore.selectedFilters$),
      map(([_, filters]) => filters)
    );
  }

  emitOnFilterButtonClick(): void {
    this.buttonFilterClicked$.next();
  }
}
